import { useState, useRef, useCallback, useEffect } from "react";
import { useAppContext } from "@/App";
import pigeonNotifier from "@/lib/pigeonNotifier";
import api from "@/lib/api";
import { Bird, Mic, MicOff, Loader2, Check, X, Send, AlertTriangle } from "lucide-react";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const LANG_SPEECH_MAP = {
  en: "en-US", nl: "nl-NL", fr: "fr-FR", de: "de-DE",
  pl: "pl-PL", ro: "ro-RO", ar: "ar-SA", zh: "zh-CN", it: "it-IT",
};

export default function VoiceDictation({ onClose, hubId, siteId, machines, sites }) {
  const { auth } = useAppContext();
  const lang = localStorage.getItem("pigeon-lang") || "en";

  const [phase, setPhase] = useState("idle"); // idle, listening, processing, preview, done, error
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [parsedRequest, setParsedRequest] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const interimRef = useRef("");

  const supported = !!SpeechRecognition;

  const startListening = useCallback(() => {
    if (!supported) { setErrorMsg("Speech recognition not supported on this browser"); return; }
    setPhase("listening");
    setTranscript("");
    setInterimText("");
    transcriptRef.current = "";
    interimRef.current = "";

    const recognition = new SpeechRecognition();
    recognition.lang = LANG_SPEECH_MAP[lang] || "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let final = "";
      let interim = "";
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      const trimmed = final.trim();
      setTranscript(trimmed);
      setInterimText(interim);
      transcriptRef.current = trimmed;
      interimRef.current = interim;
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        setErrorMsg(`Speech error: ${event.error}`);
        setPhase("error");
      }
    };

    recognition.onend = () => {
      // Auto-stop handled via stopListening
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [supported, lang]);

  const stopListening = useCallback(async () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    const finalText = transcriptRef.current || interimRef.current;
    if (!finalText.trim()) {
      setPhase("idle");
      return;
    }
    setTranscript(finalText.trim());
    setPhase("processing");

    try {
      const machineList = (machines || []).map(m => `${m.machine_number} (${m.brand || ""}, site: ${m.site_id || ""})`).join(", ");
      const siteList = (sites || []).map(s => `${s.code}: ${s.name}`).join(", ");

      const aiPrompt = `You are a logistics hub assistant. Parse this spoken request into a structured JSON object. The speaker is a machinist/on-site worker creating a request.

Available machines: ${machineList || "none listed"}
Available sites: ${siteList || "none listed"}
Current site: ${siteId || "unknown"}

Spoken text: "${finalText.trim()}"

Return ONLY a JSON object with these fields:
- request_type: one of "FUEL", "TRANSPORT", "MAINTENANCE", "REPAIR", "OTHER"
- machine_number: the machine mentioned (or "" if unclear)
- site_id: the site code mentioned (or "${siteId || ""}")
- notes: a brief description of what was requested
- urgency: "LOW", "NORMAL", or "HIGH" based on tone/context

JSON only, no explanation:`;

      const res = await api.aiChat(hubId, aiPrompt, [], lang);
      const aiText = res.reply || res.message || "";

      const jsonMatch = aiText.match(/\{[\s\S]*?\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        setParsedRequest(parsed);
        setPhase("preview");
        pigeonNotifier.speakNow(lang === "nl" ? "Aanvraag herkend" : "Request recognized");
      } else {
        setErrorMsg("Could not parse the request. Try again.");
        setPhase("error");
      }
    } catch {
      setErrorMsg("Processing failed. Check connection.");
      setPhase("error");
    }
  }, [hubId, siteId, machines, sites, lang]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const submitRequest = async () => {
    if (!parsedRequest) return;
    setPhase("processing");

    try {
      // Find matching machine
      const matchedMachine = machines?.find(m =>
        m.machine_number?.toLowerCase() === parsedRequest.machine_number?.toLowerCase()
      );

      const requestData = {
        hub_id: hubId,
        site_id: parsedRequest.site_id || siteId || "",
        machine_id: matchedMachine?.id || "",
        machine_number: parsedRequest.machine_number || "",
        request_type: parsedRequest.request_type || "FUEL",
        notes: parsedRequest.notes || transcript,
        urgency: parsedRequest.urgency || "NORMAL",
        requester_type: "MACHINIST",
        source: "VOICE_DICTATION",
      };

      await api.createRequest(requestData);
      setPhase("done");
      pigeonNotifier.speakNow(lang === "nl" ? "Aanvraag aangemaakt" : "Request created");

      // Log activity
      try {
        await api.logActivity(auth?.jwt, {
          hub_id: hubId,
          action: "REQUEST_CREATE",
          role: "machinist",
          detail: `Voice: ${parsedRequest.request_type} — ${parsedRequest.notes?.slice(0, 50)}`,
        });
      } catch (e) {}

      setTimeout(() => onClose?.(), 2000);
    } catch (err) {
      void 0;
      setErrorMsg("Failed to create request");
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center p-6 animate-fadeInUp" data-testid="voice-dictation">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bird className="w-6 h-6 text-lime-400" />
          <span className="text-xs font-black text-white uppercase tracking-widest">Voice Request</span>
        </div>
        <button data-testid="close-dictation" onClick={onClose} className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* IDLE state */}
      {phase === "idle" && (
        <div className="text-center">
          <button
            data-testid="start-dictation"
            onClick={startListening}
            className="w-32 h-32 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center shadow-2xl shadow-red-600/40 transition-all active:scale-90 mb-6"
          >
            <Mic className="w-16 h-16 text-white" strokeWidth={1.5} />
          </button>
          <p className="text-sm text-slate-400 font-bold uppercase tracking-wider">
            {lang === "nl" ? "Druk om te spreken" : "Push to speak"}
          </p>
          {!supported && (
            <p className="text-xs text-red-400 mt-2">Speech recognition not available in this browser</p>
          )}
        </div>
      )}

      {/* LISTENING state */}
      {phase === "listening" && (
        <div className="text-center w-full max-w-md">
          <div className="relative mb-8">
            <button
              data-testid="stop-dictation"
              onClick={stopListening}
              className="w-32 h-32 bg-red-600 rounded-full flex items-center justify-center shadow-2xl shadow-red-600/40 mx-auto animate-pulse"
            >
              <MicOff className="w-16 h-16 text-white" strokeWidth={1.5} />
            </button>
            {/* Ripple rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-40 h-40 border-2 border-red-500/30 rounded-full animate-ping" style={{ animationDuration: "1.5s" }} />
              <div className="absolute w-48 h-48 border border-red-500/20 rounded-full animate-ping" style={{ animationDuration: "2s" }} />
            </div>
          </div>

          {/* Live transcript */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 min-h-[80px]">
            <p className="text-white text-lg font-medium leading-relaxed">
              {transcript}
              <span className="text-slate-500">{interimText}</span>
              {!transcript && !interimText && <span className="text-slate-600 animate-pulse">Listening...</span>}
            </p>
          </div>

          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-4">
            {lang === "nl" ? "Druk nogmaals om te stoppen" : "Tap again to stop"}
          </p>
        </div>
      )}

      {/* PROCESSING state */}
      {phase === "processing" && (
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-lime-400 animate-spin mx-auto mb-6" />
          <p className="text-sm text-white font-bold uppercase tracking-wider">
            {lang === "nl" ? "Philogic verwerkt..." : "Philogic processing..."}
          </p>
          <p className="text-xs text-slate-500 mt-2">{transcript}</p>
        </div>
      )}

      {/* PREVIEW state — parsed request */}
      {phase === "preview" && parsedRequest && (
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Check className="w-12 h-12 text-lime-400 mx-auto mb-2" />
            <p className="text-sm text-white font-bold uppercase tracking-wider">Request Recognized</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Type</span>
              <span className="text-sm text-white font-black">{parsedRequest.request_type}</span>
            </div>
            {parsedRequest.machine_number && (
              <div className="flex justify-between">
                <span className="text-[10px] text-slate-500 font-bold uppercase">Machine</span>
                <span className="text-sm text-white font-bold">{parsedRequest.machine_number}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Site</span>
              <span className="text-sm text-white font-bold">{parsedRequest.site_id || siteId || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Urgency</span>
              <span className={`text-sm font-black ${parsedRequest.urgency === "HIGH" ? "text-red-400" : parsedRequest.urgency === "LOW" ? "text-slate-400" : "text-amber-400"}`}>
                {parsedRequest.urgency}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Notes</span>
              <p className="text-sm text-white/80 leading-relaxed">{parsedRequest.notes}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              data-testid="dictation-retry"
              onClick={() => { setPhase("idle"); setParsedRequest(null); }}
              className="flex-1 py-4 bg-white/10 text-white rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 hover:bg-white/20"
            >
              <Mic className="w-4 h-4" /> Retry
            </button>
            <button
              data-testid="dictation-submit"
              onClick={submitRequest}
              className="flex-1 py-4 bg-lime-500 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 hover:bg-lime-400 shadow-lg shadow-lime-500/30"
            >
              <Send className="w-4 h-4" /> Submit
            </button>
          </div>
        </div>
      )}

      {/* DONE state */}
      {phase === "done" && (
        <div className="text-center">
          <div className="w-20 h-20 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-lime-500/40">
            <Check className="w-10 h-10 text-slate-950" />
          </div>
          <p className="text-lg text-white font-black uppercase tracking-wider">
            {lang === "nl" ? "Aanvraag Aangemaakt" : "Request Created"}
          </p>
        </div>
      )}

      {/* ERROR state */}
      {phase === "error" && (
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-red-400 font-bold mb-4">{errorMsg}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setPhase("idle"); setErrorMsg(""); }} className="px-6 py-3 bg-white/10 text-white rounded-xl text-xs font-black uppercase hover:bg-white/20">
              Try Again
            </button>
            <button onClick={onClose} className="px-6 py-3 bg-red-600 text-white rounded-xl text-xs font-black uppercase hover:bg-red-500">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
